import type { QueryClient } from '@tanstack/react-query'
import type { PanoramaHttpApi } from 'api-validations/http'
import {
  getDefaultBoundaryApi,
  getFieldPlansByIdApi,
  getPlanningCffsApi
} from 'api-validations/locutus'
import { fieldsApi, flattenStreams } from 'api-validations/pangea'
import { isEmptyApplicationsError, locateByLonLatApi, RequestType } from 'api-validations/stitch'
import { streamOverviewsApi } from 'api-validations/tailor'
import { ApiError } from 'utils'

import { type ApiClient, panoramaQueryOptions } from '../../../api-provider'
import { applicationsQueryConfig } from '../../../field-data/useApplications'
import { sameCff } from '../../../operation-wide-data'
import {
  liveStreamsOnceHeard,
  useLiveStreamData
} from '../../../operation-wide-data/EmissaryWebsocketProvider'
import { liveOnlyStreams } from '../../../operation-wide-data/streamCatalog'
import { authoredAtOf, partitionKeyOf } from '../data-layer-state'
import type { AuthoredStream, CatalogStream, MapCatalog } from './catalog'

/**
 * The catalog over live data. Every API read goes through `ensureQueryData`, so
 * a lookup is served from the same cache entry the components fill and only
 * fetches what nothing has asked for yet.
 *
 * Not a hook, so that a resolution can be driven from a click handler; the
 * dependencies it needs from React are passed in once by {@link useMapCatalog}.
 */
export const createMapCatalog = ({
  queryClient,
  apiClient,
  orgCode
}: {
  queryClient: QueryClient
  apiClient: ApiClient
  orgCode: string
}): MapCatalog => {
  const read = <T extends PanoramaHttpApi.AnyApiDefinition>(
    definition: T,
    params: PanoramaHttpApi.PathParams<T>
  ) => queryClient.ensureQueryData(panoramaQueryOptions(definition, { apiClient, params }))

  const allStreams = async (): Promise<readonly CatalogStream[]> => {
    const listed = flattenStreams(await read(fieldsApi, { orgCode }))
    const liveStreams = useLiveStreamData.getState().liveAccountConfig
    return [...listed, ...liveOnlyStreams({ catalog: listed, liveStreams })]
  }

  const planningCffs = async () => (await read(getPlanningCffsApi, { orgCode })).items

  const streamById = async (streamId: string): Promise<CatalogStream | undefined> => {
    const known = (await allStreams()).find((s) => s.streamId === streamId)
    if (known !== undefined) return known
    return (await liveStreamsOnceHeard()).get(streamId)
  }

  return {
    allStreams,

    streamById,

    applicationsFor: async (streamId) => {
      try {
        return await queryClient.ensureQueryData(
          applicationsQueryConfig({
            apiClient,
            orgCode,
            streamId,
            requestType: RequestType.Default
          })
        )
      } catch (error: unknown) {
        // The response schema rejects an empty applications list; to a
        // resolution that answer is the same as an unprocessed stream's 404:
        // nothing renderable, not a failed lookup.
        if (isEmptyApplicationsError(error)) return []
        throw error
      }
    },

    recordAt: async ({ streamId, coordinate }) => {
      const located = await queryClient.ensureQueryData(
        panoramaQueryOptions(locateByLonLatApi, {
          apiClient,
          params: { orgCode, streamId, requestType: RequestType.Default },
          body: coordinate
        })
      )
      return located.record_id
    },

    planningFieldFor: async (cff) =>
      (await planningCffs()).find((candidate) => sameCff(candidate, cff))?.id ?? null,

    cffForPlanningField: async (planningFieldId) => {
      const match = (await planningCffs()).find((candidate) => candidate.id === planningFieldId)
      return match === undefined
        ? null
        : { client: match.client, farm: match.farm, field: match.field }
    },

    hasDefaultBoundary: async (planningFieldId) => {
      try {
        await read(getDefaultBoundaryApi, { orgCode, fieldId: planningFieldId })
        return true
      } catch (e: unknown) {
        // The contract documents 404 for a field with no boundary, or with only
        // inverted ones — the same answer either way for a terrain layer.
        if (e instanceof ApiError && e.status === 404) return false
        throw e
      }
    },

    planFor: async ({ planningFieldId, assetType, assetId }) => {
      const { plans } = await read(getFieldPlansByIdApi, { orgCode, fieldId: planningFieldId })
      const owning = plans.find((plan) =>
        assetType === 'Boundary'
          ? plan.boundary?.id === assetId
          : plan.prescriptions.some((prescription) => prescription.id === assetId)
      )
      return owning?.id ?? null
    },

    streamsGroupableWith: async (streamId) => {
      const stream = await streamById(streamId)
      if (stream === undefined) return []

      const scope = partitionKeyOf(stream)
      const inScope = (await allStreams()).filter((s) => partitionKeyOf(s) === scope)
      const overviews = await read(streamOverviewsApi, {
        orgCode,
        season: stream.season,
        crop: stream.cropKey
      })
      const finishedAt = new Map(overviews.items.map((row) => [row.streamId, row.timeFinished]))
      const liveStreams = useLiveStreamData.getState().liveAccountConfig

      return inScope.map((s): AuthoredStream => ({
        streamId: s.streamId,
        authoredAt: authoredAtOf({ stream: s, finishedAt, liveStreams })
      }))
    }
  }
}
